/**
 * Turn a photographed or scanned signature into something printable.
 *
 * This is done here, on a canvas, rather than by a model. The Anthropic API
 * reads images; it does not return them, so it cannot hand back a cleaned
 * picture. Everything below is deterministic, instant and free — which is also
 * what you want for something a user redoes until it looks right.
 *
 * Three steps, in order:
 *   1. Threshold on luminance — paper becomes transparent, ink stays.
 *   2. Trim the transparent border, so the mark fills its box on the form.
 *   3. Scale to a sane height and re-encode as PNG.
 */

export interface CleanedSignature {
  blob: Blob;
  width: number;
  height: number;
  /** True when thresholding found almost no ink — usually the wrong file. */
  looksEmpty: boolean;
}

/** Above this luminance a pixel is treated as paper, not ink. */
const DEFAULT_THRESHOLD = 0.72;
const TARGET_HEIGHT = 160;
const MIN_INK_RATIO = 0.0015;

export async function cleanSignatureImage(
  source: Blob,
  opts: { threshold?: number } = {},
): Promise<CleanedSignature> {
  const threshold = opts.threshold ?? DEFAULT_THRESHOLD;
  const bitmap = await createImageBitmap(source);

  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('This browser cannot process the image.');
  ctx.drawImage(bitmap, 0, 0);
  bitmap.close();

  const image = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const px = image.data;

  let minX = canvas.width;
  let minY = canvas.height;
  let maxX = -1;
  let maxY = -1;
  let ink = 0;

  for (let y = 0; y < canvas.height; y++) {
    for (let x = 0; x < canvas.width; x++) {
      const i = (y * canvas.width + x) * 4;
      // Rec. 709 luma: a mid-blue pen and mid-grey paper differ far more here
      // than in a naive channel average.
      const luma = (0.2126 * px[i]! + 0.7152 * px[i + 1]! + 0.0722 * px[i + 2]!) / 255;

      if (luma > threshold || px[i + 3]! < 16) {
        px[i + 3] = 0; // paper -> transparent
        continue;
      }

      // Ink: force it to a consistent near-black, so a faint pencil and a bold
      // marker print alike. Opacity carries the stroke's softness.
      const darkness = Math.min(1, (threshold - luma) / threshold + 0.35);
      px[i] = 17;
      px[i + 1] = 24;
      px[i + 2] = 39;
      px[i + 3] = Math.round(255 * darkness);

      ink++;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }

  const looksEmpty = ink / (canvas.width * canvas.height) < MIN_INK_RATIO;
  if (maxX < 0) {
    return { blob: await toPng(canvas), width: canvas.width, height: canvas.height, looksEmpty: true };
  }

  ctx.putImageData(image, 0, 0);

  // A few pixels of breathing room so the strokes are not clipped flush.
  const pad = 6;
  minX = Math.max(0, minX - pad);
  minY = Math.max(0, minY - pad);
  maxX = Math.min(canvas.width - 1, maxX + pad);
  maxY = Math.min(canvas.height - 1, maxY + pad);

  const cropW = maxX - minX + 1;
  const cropH = maxY - minY + 1;
  const scale = Math.min(1, TARGET_HEIGHT / cropH);

  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(cropW * scale));
  out.height = Math.max(1, Math.round(cropH * scale));
  const outCtx = out.getContext('2d');
  if (!outCtx) throw new Error('This browser cannot process the image.');
  outCtx.imageSmoothingQuality = 'high';
  outCtx.drawImage(canvas, minX, minY, cropW, cropH, 0, 0, out.width, out.height);

  return { blob: await toPng(out), width: out.width, height: out.height, looksEmpty };
}

/** Export a drawn signature from its pad, using the same trim and scaling. */
export async function cleanDrawnSignature(canvas: HTMLCanvasElement): Promise<CleanedSignature> {
  const blob = await toPng(canvas);
  // A drawn stroke is already dark on transparent, so only the trim matters —
  // a high threshold keeps every anti-aliased edge pixel.
  return cleanSignatureImage(blob, { threshold: 0.98 });
}

function toPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not encode the image.'))), 'image/png');
  });
}
